import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createSelector } from 'reselect';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import GridLayout from 'react-grid-layout';

import { getMeasurableFields } from '../../lib/exporterHelper';
import { hexToRGBA } from '../../lib/colors';
import { sortFieldPositionsByXAndYCoordinates } from '../../lib/documentHelper';

import Icon from '../misc/Icon';
import Field from './fields/Field';

import FieldModes from '../../constants/FieldModes';
import FieldTypes from '../../constants/FieldTypes';
import FieldDisplayTypes from '../../constants/FieldDisplayTypes';
import FileViews from '../../constants/FileViews';
import FieldPresentationModes from '../../constants/FieldPresentationModes';
import PageLayoutModes from '../../constants/PageLayoutModes';
import { White } from '../../constants/Colors';

import ThemeIcons from '../../theme/Icons';
import ThemeContext from '../../theme/ThemeContext';

/**
 * >>>>>>>>>>>> IMPORTANT DEFAULTS STYLES NOTICE <<<<<<<<<<<<<<<
 *
 * Do not remove the button, input, etc. or the *, ::after, etc. styles
 * those are defaults applied that the entire form is dependent on!
 *
 * These are some defaults that bootstrap includes on their form fields
 * and without these styles the form fields look messed up.
 */
const StyledIMPORTANT = styled.div`

  height: 100%;
  width: 100%;

  /*Default Form Field Styles*/

  font-size: 12px;
  font-weight: 400;
  color: ${(props) => props.theme.fontColorPrimary}; 
  line-height: 1;
  font-family:  ${(props) => props.theme.fontFamily};
  border-width: 0px !important;

  button, input, optgroup, select, textarea {
    margin: 0;
    font-family: inherit;
    line-height: inherit;
    font-size: inherit;
  }

  *, ::after, ::before {
    box-sizing: border-box;
  }

  /*React Grid Layout Styles*/

  .react-resizable-handle::after {
    display: none !important;
  };

  .react-grid-item.react-grid-placeholder {
    background-color: ${(props) => props.theme.highlightColor} !important;
    opacity: 1;
  }

  .react-grid-item > .react-resizable-handle {
    width: 18px;
    height 18px;
  }

`;

export const StyledField = styled.div`
  border-radius: 2px;
  .react-resizable-drag-handle {
    visibility: hidden;
  }
  .react-resizable-handle {
    visibility: hidden;
    background-image: none;
    padding: 0px;
  }
  :hover {
    ${(props) => (props.mode === FieldModes.edit ? `border: 1px solid ${props.theme.primaryColor};` : '')}
    ${(props) => (props.mode === FieldModes.edit ? `background-color: ${props.theme.highlightColor};` : '')} 
    z-index:9999999999 !important;
    .react-resizable-handle, .react-resizable-drag-handle {
      ${(props) => (props.mode === FieldModes.edit ? 'visibility: visible;' : '')}
    }
  }

  /*
   * IMPORTANT NOTE: Do not remove transparent border.
   *
   * Fields always have a 2px border width regardless if they are focused 
   * or not. This is to ensure that the field stays the same size 
   * when toggling in and out of focus mode and no visual shift happens.
   */
  border: 1px solid transparent;
  padding: 1px;
  ${(props) => (props.focused && props.mode === FieldModes.edit ? `border: 1px solid ${props.theme.primaryColor};` : '')}
  ${(props) => (props.zIndex ? `z-index: ${props.zIndex} !important;` : '')};
`;

export const StyledFieldItemWrapper = styled.div`
  height: 100%;
`;

const StyledResizeHandler = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -8px;
  right: -8px;
  text-align: center;
  background-color: ${(props) => props.theme.primaryColor};
  border: 1px solid ${(props) => props.theme.primaryColor};
  border-radius: 3px;
  svg {
    font-size: 12px;
    color: ${White};
    vertical-align: 0;
  }
  :hover {
    cursor: nwse-resize;
  }
`;

const StyledDragHandler = styled.div`
  width: 18px;
  height: 18px;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: -8px;
  right: -8px;
  text-align: center;
  background-color: ${(props) => props.theme.primaryColor};
  border: 1px solid ${(props) => props.theme.primaryColor};
  border-radius: 3px;
  z-index:999999999 !important;
  svg {
    font-size: 12px;
    color: ${White}
    vertical-align: 0;
  }
  :hover {
    cursor: grab;
  }
`;

/**
 * File Page
 *
 * This page targets an original page size of 816px by 1056px.
 * This represents the standard US Letter size format.
 *
 * 1 point is 1/72 of an inch.
 * Generally, 1em = 12pt = 16px = 100%.
 *
 * Points for an 8.5x11 PDF: (612x792)
 * Pixels for an 8.5x11 PDF: (816x1056)
 *
 */
class GridLayoutWrapper extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {

    id: PropTypes.string.isRequired,

    /**
     * Page properties
     */
    cols: PropTypes.number,
    rowHeight: PropTypes.number,
    width: PropTypes.number.isRequired,

    /**
     * Field properties
     */
    focused: PropTypes.string,
    fields: PropTypes.array,
    fieldDefaultStyles: PropTypes.object,

    /**
     * Layout will determine the functionality of
     * how fields can be positioned on the page.
     */
    layout: PropTypes.oneOf([
      PageLayoutModes.grid,
      PageLayoutModes.stacked,
      PageLayoutModes.float
    ]),

    /**
     * Field modes determine what way a field is supposed to behave on the page.
     *
     * For instance:
     *  - edit: mode enables resizing, drag and drop placement, etc.
     *  - fill: mode enables editing the field values
     *  - readonly: mode strips all editing capability and additional actions from the form.
     */
    mode: PropTypes.oneOf([
      FieldModes.edit,
      FieldModes.fill,
      FieldModes.readonly,
    ]),

    view: PropTypes.oneOf([
      FileViews.mobile,
      PropTypes.string
    ]),

    /**
     * Field presentation modes determine how a field appears on the page.
     *
     * For instance:
     *  - normal: displays fields in the way you expect trandional web based forms.
     *  - transparent: displays fields in a transparent way that is optimized for PDF forms.
     */
    presentation: PropTypes.oneOf([
      FieldPresentationModes.normal,
      FieldPresentationModes.transparent,
    ]),

    /**
     * Enable field virtualization for table and input group fields to
     * enhance form performance.
     */
    enableVirtualFields: PropTypes.bool,

    /**
     * Enable field lazy loading to enhance form performance.
     */
    enableLazyLoadFields: PropTypes.bool,

    //Action handlers
    onFieldSelect: PropTypes.func,
    onFieldChange: PropTypes.func,
    onFieldUpload: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onFieldLayoutsChange: PropTypes.func,
    onFieldMeasurementsCompleted: PropTypes.func,

    //Table Field Action handlers
    onTableFieldCellUpdate: PropTypes.func,
    onTableFieldCellUpload: PropTypes.func,
    onTableFieldRowAddBelow: PropTypes.func,
    onTableFieldRowDuplicate: PropTypes.func,
    onTableFieldRowMoveUp: PropTypes.func,
    onTableFieldRowMoveDown: PropTypes.func,
    onTableFieldRowDelete: PropTypes.func,

  }

  static defaultProps = {
    cols: 24,
    rowHeight: 8,
  }

  constructor(props) {
    super(props);

    this.state = { formattedFieldPositions: [] };

  }

  componentDidMount() {
    this.populateFormattedFieldPositons();
  }

  componentDidUpdate(prevProps) {

    const { fields } = this.props;

    if (prevProps.fields !== fields) {
      this.populateFormattedFieldPositons();
    }

  }

  populateFormattedFieldPositons = () => {

    const { fields } = this.props;

    /**
     * Storing formattedFieldPositions in state
     *
     * Why are formattedFieldPositions stored in state
     * and not just simply inside the render method and
     * passed to getLayout, etc.?
     *
     * For some reason getLayout method with the react-grid-container
     * loses reference to the most up to date fieldPositions with
     * it is not pulling them from state.
     *
     * If I tried to do layout={this.getLayout(formattedFieldPositions)} on
     * react-grid-layout it would reference the original passed array and not
     * the updated version in future render cycles anytime it call the layouts
     * again.
     */
    this.setState({ formattedFieldPositions: fields });

  }

  render() {

    const {
      width,
      mode,
      layout,
      cols,
      rowHeight,
    } = this.props;

    const { primaryColor, fontFamily, fontColorPrimary } = this.context;

    const ResizeHandle = React.forwardRef((props, ref) => {
      return (
        <StyledResizeHandler
          {...props}
          ref={ref}
          theme={{ primaryColor }}
          data-testid="react-resizable-handle"
          className="react-resizable-handle"
        >
          <Icon icon={ThemeIcons.misc.resize} color={White} />
        </StyledResizeHandler>
      );
    });

    //DO NOT REMOVE StyledIMPORTANT! See comment above styled component definition.
    return (
      <StyledIMPORTANT
        theme={{ fontColorPrimary, fontFamily, highlightColor: hexToRGBA(primaryColor, '0.1') }}
        id="style-defaults-important"
      >
        <GridLayout
          className="layout"
          allowOverlap={layout === PageLayoutModes.float}
          compactType="vertical"
          margin={[0, 0]}
          isDraggable={mode === FieldModes.edit}
          isResizable={mode === FieldModes.edit}
          layout={this.getLayout()}
          cols={cols}
          width={width}
          rowHeight={rowHeight}
          useCSSTransforms
          draggableHandle=".react-resizable-drag-handle"
          resizeHandle={<ResizeHandle />}
          onLayoutChange={this.handleLayoutChange}
          onResizeStop={this.handleManualLayoutChange}
          onDragStop={this.handleManualLayoutChange}
        >
          {this.getFieldNodes()}
        </GridLayout>
      </StyledIMPORTANT>
    );

  }

  getLayout = () => {

    const { formattedFieldPositions } = this.state;

    const layouts = formattedFieldPositions.map((field) => {

      //Convert joyfill position object to supported react-grid-layout object
      return {
        i: field._id,
        x: field.x,
        y: field.y,
        w: field.width,
        h: field.height
      };

    });

    return layouts;

  }

  getFieldNodes = () => {

    const { formattedFieldPositions } = this.state;

    const {
      mode,
      view,
      focused,
      fieldDefaultStyles,
      enableVirtualFields,
      enableLazyLoadFields,
      enableFieldMeasurementRendering,
      onFieldSelect,
      onTableFieldCellUpdate,
      onTableFieldCellUpload,
      onTableFieldRowAddBelow,
      onTableFieldRowDuplicate,
      onTableFieldRowMoveUp,
      onTableFieldRowMoveDown,
      onTableFieldRowDelete,
      onFieldUpload,
      layout,
      presentation
    } = this.props;

    const { primaryColor } = this.context;

    const theme = {
      ...this.context,
      highlighColor: hexToRGBA(primaryColor, '0.1'),
    };

    const tabIndexLookup = generateTabIndexLookup({ formattedFieldPositions });

    return formattedFieldPositions.map((field) => {

      const isFocused = field._id === focused;
      const isReadOnly = field.disabled || mode === FieldModes.readonly;

      return (
        <StyledField
          theme={theme}
          key={field._id}
          data-testid={`${field._id}-page-field`}
          focused={isFocused}
          mode={mode}
          zIndex={field.zIndex}
          onClick={() => { if (onFieldSelect && !isReadOnly) onFieldSelect(field._id); }}
        >
          <StyledFieldItemWrapper>
            <Field
              {...field}
              view={view}
              fieldMode={mode}
              fieldPresentationMode={presentation}
              pageLayoutMode={layout}
              tabIndex={mode === FieldModes.readonly ? -1 : tabIndexLookup[field._id]}
              disabled={isReadOnly}
              height={field.height}
              virtual={enableVirtualFields}
              lazyload={enableLazyLoadFields}
              onFocus={this.handleFieldSelect}
              onBlur={this.handleFieldBlur}
              onChange={this.handleFieldUpdate}
              onCellChange={onTableFieldCellUpdate}
              onCellUpload={onTableFieldCellUpload}
              onRowCreate={onTableFieldRowAddBelow}
              onRowAddBelow={onTableFieldRowAddBelow}
              onRowDuplicate={onTableFieldRowDuplicate}
              onRowMoveUp={onTableFieldRowMoveUp}
              onRowMoveDown={onTableFieldRowMoveDown}
              onRowDelete={onTableFieldRowDelete}
              fieldDefaultStyles={fieldDefaultStyles}
              onUpload={onFieldUpload}
              active={isFocused}
              measure={enableFieldMeasurementRendering}
              onMeasureCompleted={this.handleMeasureCompleted}
            />
          </StyledFieldItemWrapper>
          {mode !== FieldModes.edit ? null : (
            <StyledDragHandler theme={this.context} data-testid={`${field._id}-react-resizable-drag-handle`} className="react-resizable-drag-handle">
              <Icon icon={ThemeIcons.misc.drag} color={White} />
            </StyledDragHandler>
          )}
        </StyledField>
      );
    });

  }

  handleMeasureCompleted = (fieldId, fieldIdentifier, fieldPositionId, rect) => {

    const { formattedFieldPositions } = this.state;
    const { rowHeight, onFieldMeasurementsCompleted } = this.props;

    const measureableFields = getMeasurableFieldSelector({ formattedFieldPositions });
    const targetFieldPosition = getFieldPositionByFieldPositionId({ formattedFieldPositions, fieldPositionId });

    if (!this.measurements) this.measurements = {};

    /**
     * Handle measured height
     *
     * If measuredHeight is less than current fieldPosition height then no adjustment is needed
     * Convert to proper react-grid-layout row height
     *
     * Why +4? The field has a transparent border of 2px all around
     * that is not accounted for in the internal height calculation.
     */
    let measuredHeight = (rect.height + 4) / rowHeight;
    if (measuredHeight < targetFieldPosition.height) measuredHeight = targetFieldPosition.height;

    const nextRect = { ...rect, width: Math.ceil(rect.width + 4), height: Math.ceil(measuredHeight) };
    this.measurements[fieldPositionId] = nextRect;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (Object.keys(this.measurements).length === measureableFields.length && onFieldMeasurementsCompleted) {
      onFieldMeasurementsCompleted(this.measurements);
    }

  }

  handleFieldSelect = (fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier) => {

    const { onFieldSelect, onFocus } = this.props;

    /**
     * We use the fieldPositionId to determine whether a field is currently focused or not
     */
    if (onFieldSelect) onFieldSelect(fieldPositionId);

    if (onFocus) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier);
    }

  }

  handleFieldBlur = (fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier) => {

    const { onBlur } = this.props;

    if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, e, rowId, columnId, columnIdentifier);

  }

  handleFieldUpdate = (fieldId, fieldIdentifier, fieldPositionId, value, signer) => {

    const { onFieldChange } = this.props;

    const data = { value };
    if (signer) data.signer = signer;

    /**
     * The loop below prevents the chart titles and max min coordinates
     * from being passed as a value
     */
    if (value
      && onFieldChange
      && (value.hasOwnProperty('xTitle')
        || value.hasOwnProperty('xMin')
        || value.hasOwnProperty('xMax')
        || value.hasOwnProperty('yTitle')
        || value.hasOwnProperty('yMin')
        || value.hasOwnProperty('yMax'))) {

      onFieldChange(fieldId, fieldIdentifier, fieldPositionId, value);
    }
    else if (onFieldChange) {
      onFieldChange(fieldId, fieldIdentifier, fieldPositionId, data);
    }
  }

  /**
   * React-grid-layout doesn't call onLayoutChange prop when allowOverlap (layout === float)
   * props is set to true.
   *
   * We manually call on onLayoutChange here when layout is float
   * (float means allowOverlap on react-grid-layout will be true) to trigger the
   * field layout update action handler so that it gets fired everytime there is
   * a change regardless of the layout set. This ensures it consistently fires
   * if someone has logic that is dependent on it firing.
   */
  handleManualLayoutChange = (layouts) => {

    const { layout } = this.props;
    if (layout === PageLayoutModes.float) this.handleLayoutChange(layouts);

  }

  handleLayoutChange = (layouts) => {

    const { onFieldLayoutsChange } = this.props;
    const { formattedFieldPositions } = this.state;

    /**
     * Check if layouts actually changed.
     *
     * React-grid-layout fires onLayoutChange when the component initially loads. We
     * do this check to prevent unnecessary updates. This ensures we fire onLayoutChange
     * only when position values actually change.
     */
    const currentLayouts = this.getLayout();
    if (!hasLayoutChanged(currentLayouts, layouts)) return;

    if (onFieldLayoutsChange) {

      /**
       * Add identifier to fieldPosition layout objects
       */
      const fieldPositionLookup = {};
      formattedFieldPositions.forEach((fieldPosition) => fieldPositionLookup[fieldPosition._id] = fieldPosition);

      const nextLayouts = layouts.map((layout) => {
        const formattedLayoutObject = getFormattedLayoutObject(layout);
        return { ...formattedLayoutObject };
      });

      onFieldLayoutsChange(nextLayouts);

    }

  }

}

/**
 * Properly format react-grid-layout props to supported joyfill props.
 *
 * @param {Object} reactGridLayout
 * @returns {Object}
 */
export const getFormattedLayoutObject = (reactGridLayout) => {
  return {
    _id: reactGridLayout.i,
    width: reactGridLayout.w,
    height: reactGridLayout.h,
    x: reactGridLayout.x,
    y: reactGridLayout.y
  };
};

/**
 * Check if the grid layout has changed
 *
 * @param {Array} currentLayouts //Raw react-grid layout objects
 * @param {Array} nextLayouts //Raw react-grid layout objects
 * @return {Boolean}
 */
export const hasLayoutChanged = (currentLayouts, nextLayouts) => {

  const currentLayoutLookup = {};
  currentLayouts.forEach((layout) => currentLayoutLookup[layout.i] = layout);

  const changedLayout = nextLayouts.find((nextLayout) => {
    const currentLayout = currentLayoutLookup[nextLayout.i];
    if (!currentLayout) return true;
    if (currentLayout.x !== nextLayout.x) return true;
    if (currentLayout.y !== nextLayout.y) return true;
    if (currentLayout.w !== nextLayout.w) return true;
    if (currentLayout.h !== nextLayout.h) return true;
    return false;
  });

  return !!changedLayout;

};

export const generateTabIndexLookup = createSelector(
  (state) => state.formattedFieldPositions,
  (fields) => {

    const nextFields = [...fields];

    const tabIndexLookup = {};

    //Filter out any fields that should not receive tab index
    const tabbableFields = nextFields.filter((field) => {

      if (field.type !== FieldTypes.block
        && field.type !== FieldTypes.image
        && field.type !== FieldTypes.signature
        && field.type !== FieldTypes.richText
        && field.displayType === FieldDisplayTypes.original
      ) {
        return true;
      } else {
        return false;
      }
    });

    sortFieldPositionsByXAndYCoordinates(tabbableFields).forEach((field, i) => {
      //We do +1 because we don't want tabindex to start at 0. First tabindex should start at 1.
      tabIndexLookup[field._id] = i + 1;
    });

    return tabIndexLookup;

  }

);

export const getMeasurableFieldSelector = createSelector(
  (state) => state.formattedFieldPositions,
  (fieldPositions) => {
    return getMeasurableFields(fieldPositions);
  }
);

export const getFieldPositionByFieldPositionId = createSelector(
  (state) => state.formattedFieldPositions,
  (state) => state.fieldPositionId,
  (fieldPositions, fieldPositionId) => {
    return fieldPositions.find((fieldPosition) => fieldPosition._id === fieldPositionId);
  }
);

export default GridLayoutWrapper;
